/**
 * Recent flight searches — last 5 stored in localStorage, **per logged-in user**.
 *
 * Each user gets their own bucket so the cards never leak between accounts on
 * a shared browser. Guests (not signed in) share a `guest` bucket; on sign-in
 * we deliberately do NOT promote the guest history to the user — keeping a
 * clean separation between anonymous browsing and authenticated activity.
 *
 *   Key shape: `fa_recent_searches_v2:<userId>`  (`<userId>="guest"` if logged out)
 *
 * The widget passes `user?.id` into every call (see FlightSearchWidget.jsx).
 */
const PREFIX = "fa_recent_searches_v2";
const LEGACY_KEY = "fa_recent_searches_v1";   // old single-bucket key — purged on first read
const MAX = 5;

const keyFor = (userId) => `${PREFIX}:${userId || "guest"}`;

// One-time migration: wipe the legacy single-bucket key so old guests don't
// see the previous user's history.
let _migrated = false;
const _migrate = () => {
  if (_migrated) return;
  try { localStorage.removeItem(LEGACY_KEY); } catch { /* no-op */ }
  _migrated = true;
};

export const recentSearches = {
  list(userId) {
    _migrate();
    try { return JSON.parse(localStorage.getItem(keyFor(userId))) || []; } catch { return []; }
  },
  push(criteria, userId) {
    if (!criteria?.segments?.[0]?.from || !criteria.segments[0].to) return;
    _migrate();
    const next = [criteria, ...recentSearches.list(userId).filter((c) => {
      const a = c.segments?.[0], b = criteria.segments?.[0];
      return !(a && b && a.from === b.from && a.to === b.to && a.date === b.date
               && c.cabin === criteria.cabin && c.passengers === criteria.passengers);
    })].slice(0, MAX);
    localStorage.setItem(keyFor(userId), JSON.stringify(next));
  },
  clear(userId) { localStorage.removeItem(keyFor(userId)); },
};
